import { Anchor, Pager, SearchBar, Table, TableColumn, TableRow } from '@keplerco/core';
import React, { useEffect, useState } from 'react';
import { useAppState, useAppActions } from '../../../../../overmind';
import { generateUniqueSkillNames, transformAverageScores, transformScores } from './role-competency-matrix.helpers';
import { PagePath } from '../../../../../navigation/navigation.enums';
import { EmptyState } from '../../../../../components/general/empty-state/empty-state';
import { useKeplerNavigate } from '../../../../../navigation/guards/use-kepler-navigate';
import { DataPoint } from '../../../../../components/charts/recharts.models';
import { ScoresSearchParams } from '../../../../../models/overmind/search-params';
import { SkeletonOverlay } from '../../../../../components/general/loading-state/skeleton-overlay';
import classNames from 'classnames';
import { WidgetCard } from '../../../analytics-filters/widget-card';
import { OrganizationLevelType } from '../../../../../enums';
import { SORT_BY_OPTIONS } from '../../../../../library/consts/consts';
import { SortBy } from '../../../../../components/general/sort-by/sort-by';
import { SortByOption } from '../../../../../enums/sort-by-option';

export function RoleCompetencyMatrixWidget({ baseSearchParams }: { baseSearchParams: ScoresSearchParams }): React.ReactElement {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [scoresData, setScoresData] = useState<DataPoint[]>([]);
  const [averageScoreData, setAverageScoreData] = useState<DataPoint[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [searchParams, setSearchParams] = useState<ScoresSearchParams>(baseSearchParams);

  async function getData(searchParams: ScoresSearchParams) {
    const [averageScoresData, scoreData] = await Promise.all([
      actions.analyticsGetScoresByOrganizationLevel({ ...searchParams }),
      actions.analyticsGetScoresByOrganizationLevel({ ...searchParams, organizationLevel: OrganizationLevelType.Learner }),
    ]);

    const uniqueSkillNames = generateUniqueSkillNames(averageScoresData?.results ?? []);

    setAverageScoreData(transformAverageScores(averageScoresData?.results ?? [], companyVariables.useLevels));
    setScoresData(transformScores(scoreData?.results ?? [], uniqueSkillNames, companyVariables.useLevels));
    setTotalPages(scoreData?.totalPages ?? 0);
  }

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      await getData(searchParams);
      setIsLoading(false);
    }

    initialise();
  }, [baseSearchParams.roleSlug, baseSearchParams.learnerSlug, companyVariables.slug]);

  return (
    <SkeletonOverlay isLoading={isLoading} width='100%'>
      <WidgetCard title="Role competency matrix " subtitle="A view of competencies across the role skills, providing insights into capability distribution and potential skill gaps within the role assignees." showSkillLevelHelp>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
          <SearchBar
            label="Search"
            loading={isSearching}
            onInput={async event => {
              setIsSearching(true);
              const search = (event.target as HTMLInputElement).value;
              setSearchParams({ ...searchParams, search, page: 1 });
              await getData({ ...searchParams, search, page: 1 });
              setIsSearching(false);
            }}
          />

          <SortBy
            options={SORT_BY_OPTIONS.filter(item => item.value === SortByOption.AlphabeticalAtoZ || item.value === SortByOption.AlphabeticalZtoA)}
            value={searchParams.sortAscending ? SortByOption.AlphabeticalAtoZ : SortByOption.AlphabeticalZtoA}
            onChange={async event => {
              setIsLoading(true);
              const sortAscending = event.target.value === SortByOption.AlphabeticalAtoZ;
              setSearchParams({ ...searchParams, sortAscending });
              await getData({ ...searchParams, sortAscending });
              setIsLoading(false);
            }}
          />
        </div>

        <Table
          emptyState={<EmptyState title="Hang tight!" subtitle="We are waiting for data." />}
          footerContent={
            <div className={classNames({ invisible: !totalPages || totalPages < 2 })}>
              <Pager
                activePageNumber={searchParams.page ?? 1}
                pageCount={totalPages}
                onPageChange={async page => {
                  if (page === searchParams.page) return;
                  setIsLoading(true);
                  setSearchParams({ ...searchParams, page });
                  await getData({ ...searchParams, page });
                  setIsLoading(false);
                }}
              />
            </div>
          }
        >
          {(!!scoresData && !!averageScoreData ? [...scoresData, ...averageScoreData] : []).map((score, index) => (
            <TableRow key={score.name} id={score.name!}>
              {Object.entries(score)
                .filter(([key]) => key !== 'slug')
                .map(([key, value]) => (
                  <TableColumn
                    key={score.slug}
                    id={key}
                    locked={key === 'name'}
                    label={key === 'name' ? 'Name' : key}
                    heatmap={{
                      score: typeof value === 'number' ? Number(value) : String(value),
                      min: companyVariables.useLevels ? companyVariables.minLevel : 0,
                      max: companyVariables.useLevels ? companyVariables.maxLevel : 100,
                    }}
                    fontWeight={index === scoresData?.length ? '700' : 'inherit'}
                    width={220}
                  >
                    {!!score.slug && index !== scoresData?.length && key === 'name' ? (
                      <Anchor
                        hovertype="opacity"
                        textTransform="none"
                        onClick={event => {
                          event.stopPropagation();
                          if (!searchParams.roleSlug || !score.slug) return;
                          const path = PagePath.analyticsRolePerson.replace(':companySlug', companyVariables.slug!).replace(':roleSlug', searchParams.roleSlug).replace(':personSlug', String(score.slug));
                          keplerNavigate(`${PagePath.analyticsBase}${path}`);
                        }}
                      >
                        {value}
                      </Anchor>
                    ) : (
                      value
                    )}
                  </TableColumn>
                ))}
            </TableRow>
          ))}
        </Table>
      </WidgetCard>
    </SkeletonOverlay>
  );
}
